/**
 * @file vector-tools-handler.ts
 * @description Manages vector-based drawing tools (Line, Circle, Rect, Ellipse) and canvas transformation tools (Move, Crop).
 */

import { g, Tool } from '@hcie/core';
import { 
    drawLine, 
    drawCircle, 
    drawRect, 
    drawRoundedRect, 
    drawEllipse, 
    moveSelectionContent, 
    moveSelectionMask 
} from '@hcie/tools';
import { getActiveLayer } from '../layer-service';
import { tempCtx } from '../rendering-loop';

export class VectorToolsHandler {
    /**
     * @function handleMouseDown
     * @description Initiates a vector drawing or transformation operation.
     * @param {MouseEvent} event - The native mouse event.
     * @returns {boolean} - Returns true if the tool handled the event.
     */
    public static handleMouseDown(event: MouseEvent): boolean {
        // [CRITICAL_LOGIC_START]
        const toolId: string = g.current_tool.id;
        const activeLayer = getActiveLayer();
        
        if (!activeLayer || !activeLayer.visible) {
            return false;
        }

        // CASE A: Selection Movement (Move Mask or Move Content)
        if ([Tool.MoveSelection.id, Tool.MoveContent.id].includes(toolId)) {
            if (g.isSelectionActive && g.selectionCanvas) {
                g.movingSelection = true;
                if (toolId === Tool.MoveContent.id && typeof moveSelectionContent === 'function') {
                    moveSelectionContent(0, 0); // Initialize content buffer
                }
                return true;
            }
            return false;
        }

        // CASE B: Standard Vector Shapes
        const isVectorShapeTool: boolean = [
            Tool.Line.id, 
            Tool.Circle.id, 
            Tool.Rectangle.id, 
            Tool.Rounded_Rectangle.id, 
            Tool.Ellipse.id
        ].includes(toolId);

        if (isVectorShapeTool) {
            this.prepareVectorDrawing(activeLayer);
            return true;
        }

        // CASE C: Cropping
        if (toolId === Tool.Crop.id) {
            this.handleCropInteractionDown();
            return true;
        }

        return false;
        // [CRITICAL_LOGIC_END]
    }

    /**
     * @function handleMouseMove
     * @description Updates the vector preview or crop box while dragging.
     * @param {MouseEvent} event - The native mouse event.
     * @returns {boolean} - Returns true if handled.
     */
    public static handleMouseMove(event: MouseEvent): boolean {
        const toolId: string = g.current_tool.id;
        const isResizing: boolean = (g as any)._isResizingCrop || (g as any)._isMovingCrop;
        const isVectorTool: boolean = [
            Tool.Line.id, Tool.Circle.id, Tool.Rectangle.id, 
            Tool.Rounded_Rectangle.id, Tool.Ellipse.id
        ].includes(toolId);

        if (!isVectorTool && !isResizing && !g.movingSelection) return false;
        if (!g.drawing && !isResizing && !g.movingSelection) return false;
        
        const activeLayer = getActiveLayer();
        if (!activeLayer || !activeLayer.ctx) return false;

        // [CRITICAL_LOGIC_START]
        // STEP 1: Angle/Ratio Snapping (Shift Key)
        if (event.shiftKey) {
            this.applyCoordinateSnapping(toolId);
        }

        const temporaryContext = tempCtx as CanvasRenderingContext2D;
        if (temporaryContext) {
            temporaryContext.clearRect(0, 0, temporaryContext.canvas.width, temporaryContext.canvas.height);
        }

        // STEP 2: Selection/Content Movement
        if (g.movingSelection && (toolId === Tool.MoveSelection.id || toolId === Tool.MoveContent.id)) {
            this.dispatchMovementUpdate(toolId);
            return true;
        }

        // STEP 3: Vector Shape Preview
        return this.dispatchVectorDrawing(toolId, event, temporaryContext);
        // [CRITICAL_LOGIC_END]
    }

    // --- Private Helper Methods ---

    /**
     * @function prepareVectorDrawing
     * @private
     */
    private static prepareVectorDrawing(activeLayer: any): void {
        if (activeLayer.type !== 'vector') {
            (window as any).tooltipManager?.showWarning?.('Vector Tool on Raster Layer');
        }
        g.drawing = true;
    }

    /**
     * @function handleCropInteractionDown
     * @private
     */
    private static handleCropInteractionDown(): void {
        const cropRect = (g as any).lastCropRect;
        if (!cropRect) return;

        const handleTolerance: number = 15;
        const handles = [
            { x: cropRect.x, y: cropRect.y }, { x: cropRect.x + cropRect.w / 2, y: cropRect.y }, { x: cropRect.x + cropRect.w, y: cropRect.y },
            { x: cropRect.x, y: cropRect.y + cropRect.h / 2 }, { x: cropRect.x + cropRect.w, y: cropRect.y + cropRect.h / 2 },
            { x: cropRect.x, y: cropRect.y + cropRect.h }, { x: cropRect.x + cropRect.w / 2, y: cropRect.y + cropRect.h }, { x: cropRect.x + cropRect.w, y: cropRect.y + cropRect.h }
        ];

        let targetHandleIndex: number = -1;
        for (let i = 0; i < handles.length; i++) {
            if (Math.abs(g.pX - handles[i].x) < handleTolerance && Math.abs(g.pY - handles[i].y) < handleTolerance) {
                targetHandleIndex = i;
                break;
            }
        }

        if (targetHandleIndex !== -1) {
            (g as any)._isResizingCrop = true;
            (g as any)._cropResizeHandle = targetHandleIndex;
            (g as any)._cropRefRect = { ...cropRect };
        } else if (g.pX >= cropRect.x && g.pX <= cropRect.x + cropRect.w && g.pY >= cropRect.y && g.pY <= cropRect.y + cropRect.h) {
            (g as any)._isMovingCrop = true;
            (g as any)._cropRefRect = { ...cropRect };
        }
    }

    /**
     * @function applyCoordinateSnapping
     * @description Constrains mouse coordinates to specific angles (lines) or ratios (rects).
     * @private
     */
    private static applyCoordinateSnapping(toolId: string): void {
        const deltaX: number = g.pX - g.startX;
        const deltaY: number = g.pY - g.startY;

        if (toolId === Tool.Line.id) {
            const distance = Math.hypot(deltaX, deltaY);
            if (distance > 0) {
                const angleDegree: number = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
                const snapPoints: readonly number[] = [0, 30, 45, 60, 90, 120, 135, 150, 180, -30, -45, -60, -90, -120, -135, -150];
                const nearestSnap: number = snapPoints.reduce((prev, curr) => Math.abs(curr - angleDegree) < Math.abs(prev - angleDegree) ? curr : prev);
                const snapRadians: number = nearestSnap * Math.PI / 180;
                
                g.pX = g.startX + Math.cos(snapRadians) * distance;
                g.pY = g.startY + Math.sin(snapRadians) * distance;
            }
        } else if ([Tool.Rectangle.id, Tool.Rounded_Rectangle.id, Tool.Ellipse.id].includes(toolId)) {
            const sideLength: number = Math.max(Math.abs(deltaX), Math.abs(deltaY));
            g.pX = g.startX + (deltaX < 0 ? -sideLength : sideLength);
            g.pY = g.startY + (deltaY < 0 ? -sideLength : sideLength);
        }
    }

    /**
     * @function dispatchMovementUpdate
     * @private
     */
    private static dispatchMovementUpdate(toolId: string): void {
        const deltaX: number = Math.round(g.pX - g.startX);
        const deltaY: number = Math.round(g.pY - g.startY);
        
        if (deltaX === 0 && deltaY === 0) return;

        if (toolId === Tool.MoveContent.id && typeof moveSelectionContent === 'function') {
            moveSelectionContent(deltaX, deltaY);
        } else if (toolId === Tool.MoveSelection.id && typeof moveSelectionMask === 'function') {
            moveSelectionMask(deltaX, deltaY);
            // Sync ghost edit handles
            const editBounds = (g as any)._selectionEditBounds;
            if (editBounds) {
                editBounds.x1 += deltaX; editBounds.x2 += deltaX; 
                editBounds.y1 += deltaY; editBounds.y2 += deltaY;
            }
        }
        g.startX = g.pX;
        g.startY = g.pY;
    }

    /**
     * @function dispatchVectorDrawing
     * @private
     */
    private static dispatchVectorDrawing(toolId: string, event: MouseEvent, ctx: CanvasRenderingContext2D): boolean {
        switch (toolId) {
            case Tool.Line.id:
                drawLine(event, ctx);
                return true;
            case Tool.Rectangle.id:
                drawRect(ctx, g.startX, g.startY, g.pX, g.pY);
                return true;
            case Tool.Circle.id:
                drawCircle(event, ctx);
                return true;
            case Tool.Rounded_Rectangle.id:
                drawRoundedRect(ctx, g.startX, g.startY, g.pX, g.pY);
                return true;
            case Tool.Ellipse.id:
                drawEllipse(ctx, g.startX, g.startY, g.pX, g.pY);
                return true;
            default:
                return false;
        }
    }
}
